import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'
import { useAuth } from '@renderer/chat/providers'
import type { AskUserQuestionAnswer } from '@renderer/chat/integrations/ask-user-question'
import { STAGED_ACTIONS_QUERY_KEY, stagedActionExpandedQueryKey } from './staged-actions/utils'

/**
 * The gateway fills the action's fields from the answers, switching its
 * platform in place if another was picked, then drafts it. A dismissal only
 * retires the questions.
 */
export function useAnswerActionQuestions() {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({ actionId, answer }: { actionId: string; answer: AskUserQuestionAnswer }) => {
      const token = await getAuthToken()
      const response = await fetch(getChatApiRoute('api/v1/planner-v2/answer'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ stagedActionId: actionId, answer }),
      })
      if (!response.ok) {
        throw new Error(`Failed to answer the action's questions (status ${response.status})`)
      }
    },
    // The view reads its own expanded copy of the action, which the list refetch doesn't touch
    onSettled: (_data, _error, { actionId }) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: stagedActionExpandedQueryKey(actionId) }),
        queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] }),
      ]),
  })

  // Throws on failure, matching `AskUserQuestionCard`'s `onSubmit` contract (Submit stays up for retry).
  const answerQuestions = useCallback(
    async (actionId: string, answer: AskUserQuestionAnswer): Promise<void> => {
      await mutation.mutateAsync({ actionId, answer })
    },
    [mutation],
  )

  return { answerQuestions, isAnswering: mutation.isPending }
}
